import { useCallback, useEffect, useMemo, useState } from "react"
import { SaveChangesButton } from "@/styles/StandardButtons"
import { GenericSelect } from "@/styles/GenericForm"
import { getApiUrl } from "@/lib/api-config"
import { useAdminGuard } from "../AdminOnlyGuard"

interface TimezoneState {
  timezone: string | null
  confirmed: boolean
  confirmed_at: string | null
  available: boolean
}

// Fallback for a browser without Intl.supportedValuesOf -- the list is only what can be PICKED; the saved zone is always validated by the server.
const FALLBACK_ZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "America/Mexico_City", "America/Bogota", "America/Sao_Paulo", "Europe/London", "Europe/Madrid", "Europe/Paris", "Europe/Berlin",
]

/** Offset from GMT in minutes right now (so a zone in daylight time shows its current offset), or null where the browser cannot tell. */
export function zoneOffsetMinutes(zone: string, at: Date = new Date()): number | null {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(at).find((p) => p.type === "timeZoneName")
    if (!part) return null
    if (part.value === "GMT") return 0
    const m = part.value.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/)
    return m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : null
  } catch {
    return null
  }
}

/** "(-5:00 GMT) America Bogota" -- the offset first, so the list reads in numeric order. */
export function zoneLabel(zone: string, offset: number | null): string {
  const name = zone.replace(/_/g, " ").replace(/\//g, " ")
  if (offset === null) return name
  const abs = Math.abs(offset)
  return `(${offset < 0 ? "-" : "+"}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")} GMT) ${name}`
}

/** Every zone as a select option, ordered by offset from the most negative to the most positive (then by name). */
export function timeZoneOptions(zones: string[] = listTimeZones(), at: Date = new Date()): { value: string; label: string }[] {
  return zones
    .map((zone) => ({ zone, offset: zoneOffsetMinutes(zone, at) }))
    .sort((a, b) => (a.offset ?? 99999) - (b.offset ?? 99999) || a.zone.localeCompare(b.zone))
    .map(({ zone, offset }) => ({ value: zone, label: zoneLabel(zone, offset) }))
}

export function listTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  const zones = typeof intl.supportedValuesOf === "function" ? intl.supportedValuesOf("timeZone") : FALLBACK_ZONES
  return zones.includes("UTC") ? zones : ["UTC", ...zones]
}

/**
 * Settings -> Company -> Time zone. The company's IANA zone is what Bob's schedule is read in -- never the browser's or the server's. It is NOT
 * filled in on its own: until an admin saves one it stays "not set". The browser's zone is offered as a suggestion only.
 */
export function TimezoneSection() {
  const canApply = useAdminGuard()
  const [state, setState] = useState<TimezoneState | null>(null)
  const [draft, setDraft] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(getApiUrl("api/company/timezone"))
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body?.error?.message || body?.message || body?.error || "The time zone could not be loaded")
      setState(body.data as TimezoneState)
      setDraft((body.data as TimezoneState).timezone || "")
    } catch (err) {
      setError(err instanceof Error ? err.message : "The time zone could not be loaded")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const options = useMemo(() => timeZoneOptions(), [])
  const browserZone = useMemo(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return "" } }, [])

  const save = async () => {
    if (!canApply()) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const response = await fetch(getApiUrl("api/company/timezone"), { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ timezone: draft }) })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body?.error?.message || body?.message || body?.error || "The time zone could not be saved")
      setState(body.data as TimezoneState)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "The time zone could not be saved")
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="mt-8 border-t border-gray-200 pt-6"><p className="text-sm text-gray-500">Loading time zone...</p></div>

  const dirty = draft !== (state?.timezone || "")

  return (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="timezone-section">
      <h3 className="font-medium mb-1">Time zone</h3>
      <p className="text-sm text-gray-500 mb-4">
        The company&apos;s time zone. Bob&apos;s daily review is scheduled in this zone, not in the browser&apos;s or the server&apos;s.
      </p>

      {error && <div className="error-message mb-3" role="alert">{error} <button type="button" className="underline" onClick={() => void load()}>Retry</button></div>}
      {state && !state.available && <p className="text-sm text-amber-700 mb-3">The time zone setting is not available yet.</p>}
      {state && state.available && !state.confirmed && (
        <p className="text-sm text-amber-700 mb-3" role="status">Not set. Bob will not schedule anything until an admin confirms a time zone.</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <GenericSelect id="company_timezone" value={draft} options={options} placeholder="Select a time zone" searchable disabled={saving || !state?.available} onValueChange={(zone) => { setSaved(false); setDraft(zone) }} />
        </div>
        {(
          <SaveChangesButton type="button" onClick={() => void save()} disabled={!dirty || !draft || saving || !state?.available}>
            {saving ? "Saving…" : "Save time zone"}
          </SaveChangesButton>
        )}
        {saved && !dirty && <span className="text-sm text-gray-500" role="status">Saved</span>}
      </div>

      {browserZone && browserZone !== draft && state?.available && (
        <p className="text-xs text-gray-500 mt-2">
          This browser reports {browserZone}. <button type="button" className="underline" onClick={() => { setSaved(false); setDraft(browserZone) }}>Use it</button> (it is only filled in; nothing is saved until you press Save).
        </p>
      )}
    </div>
  )
}
